import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {DirectoryTree} from '../../components/DirectoryTree';
import {FileList} from '../../components/FileList';
import {usePath} from '../../hooks/usePath';
import {Breadcrumb} from '../../components/Breacrumb';
import {FloatingAction} from '../../components/FloatingAction';
import {useSelection} from '../../hooks/useSelection';
import {ActionBar} from '../../components/ActionBar';
import {NameModal} from '../../components/NameModal';
import {UploadPanel} from '../../components/UploadPanel';
import {useQueryClient} from '@tanstack/react-query';
import {directoryService} from '../../services/directory';
import {useDelete} from '../../hooks/useDelete';
import {useMove} from '../../hooks/useMove';
import {Button, message, Switch, Tooltip} from 'antd';
import {MoveModal} from '../../components/MoveModal';
import type {FileItem, PermissionCheckParams} from '../../types/directory';
import {FileMetaModal} from '../../components/FileMetaModal';
import {useUpdateFileMeta} from '../../hooks/useUpdateFileMeta';
import {SearchOverlay} from '../../components/SearchOverlay';
import './index.scss';
import {getRootIdFromUrl} from '../../utils/getRootIdFromUrl';
import {buildSegmentsFromPathIds, getPathIdsFromUrl, getPathName} from '../../utils/path';
import {useCurrentUser} from '../../hooks/useCurrentUser';
import {useUploadStore} from '../../store/uploadStore';
import {usePermissionCheck} from '../../hooks/usePermissionCheck';
import {ReloadOutlined} from '@ant-design/icons';
import {ShareScopeModal} from '../../components/ShareScopeModal';

export function FileDrive() {
  const queryClient = useQueryClient();
  const {
    open: uploadOpen,
    minimized: uploadMinimized,
    mode: uploadMode,
    setOpen: setUploadOpen,
    setMinimized: setUploadMinimized,
    setMode: setUploadMode,
    closeUpload
  } = useUploadStore();
  const {current, segments, initRoot, changePath, restorePath, setCurrentOnly, resetPath} = usePath();
  const currentId = current?.id;
  const isOrganizationLevel = segments.length === 1;
  const accessPath = segments.map(item => item.id).join(',');
  const {data: currentUser} = useCurrentUser();
  const selection = useSelection();
  const {selectedItems, clear} = selection;
  const deleteMutation = useDelete(currentId, clear);
  const moveMutation = useMove(currentId);
  const permissionCheck = usePermissionCheck(currentId, currentUser);
  const updateFileMetaMutation = useUpdateFileMeta(currentId);
  const initialPathIds = useMemo(() => getPathIdsFromUrl(), []);
  const initialCurrentId = initialPathIds[initialPathIds.length - 1];

  const [createOpen, setCreateOpen] = useState(false);
  const [singleItem, setSingleItem] = useState<FileItem | null>(null);
  const [metaOpen, setMetaOpen] = useState(false);
  const [metaModalTitle, setMetaModalTitle] = useState('编辑文件信息');
  const [renameOpen, setRenameOpen] = useState(false);
  const [metaFileInfo, setMetaFileInfo] = useState<any>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [autoEntered, setAutoEntered] = useState(false);
  const [metaReadonly, setMetaReadonly] = useState(false);
  const [myFileRootId, setMyFileRootId] = useState<number | string | null>(null);
  const initialRestoreRef = useRef(false);
  const restoredRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);
  const [currentDirectory, setCurrentDirectory] = useState<any>(null);
  const [searchHasResult, setSearchHasResult] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareRecord, setShareRecord] = useState<any>(null);
  const [hideSharedContent, setHideSharedContent] = useState(false);
  const [sharedStatusLoading, setShareStatusLoading] = useState(false);

  const isMyFileLevel =
    myFileRootId != null &&
    segments.some(item => String(item.id) === String(myFileRootId));
  const rootId = getRootIdFromUrl();

  const canWriteCurrentDirectory =
    (isMyFileLevel ||
      currentUser?.is_admin ||
      Number(currentDirectory?.owner_id) === Number(currentUser?.user_id) ||
      currentDirectory?.visibility === 'edit') &&
    !currentDirectory?.is_shared;

  async function handleCreate(name: string) {
    if (!currentId) return;
    if (!name.trim()) return;

    await directoryService.create({
      parent_id: currentId,
      name
    });

    await queryClient.invalidateQueries({
      queryKey: ['file-list', currentId]
    });

    await queryClient.invalidateQueries({
      queryKey: ['directory-tree']
    });

    setCreateOpen(false);
  }

  async function handleRefresh() {
    if (!currentId) return;

    await queryClient.invalidateQueries({
      queryKey: ['file-list', currentId]
    });
  }

  async function handleSharedStatusChange(checked: boolean) {
    if (!currentId) return;

    const nextHideSharedContent = checked;
    const status = nextHideSharedContent ? 1 : 0;

    setHideSharedContent(nextHideSharedContent);
    setShareStatusLoading(true);

    try {
      await directoryService.updateSharedStatus({status});

      await queryClient.invalidateQueries({
        queryKey: ['file-list', currentId],
      });
    } catch (e) {
      setHideSharedContent(!nextHideSharedContent);
      message.error('更新共享内容显示状态失败');
    } finally {
      setShareStatusLoading(false);
    }
  }

  async function handleDelete(items: FileItem[]) {
    const pass = await permissionCheck.check(
      items.map(item => ({
        id: item.id,
        type: item.type
      })),
    );

    if (!pass) {
      clear();
      return;
    }

    deleteMutation.mutate({item: items});
  }

  async function handleMove(target: { id: number | string; level: number | null }) {
    if (!currentId) return;

    const targetId = target.id;
    const targetLevel = target.level;

    const items = singleItem ? [singleItem] : selectedItems;

    if (!items.length) {
      void message.warning('请选择移动项');
      return;
    }

    if (targetId === currentId) {
      void message.warning('不能移动到当前目录');
      return;
    }

    if (items.some(i => i.id === targetId)) {
      void message.warning('不能移动到自身');
      return;
    }

    const isMoveToOrganizationLevel = targetLevel === 0;

    if (isMoveToOrganizationLevel) {
      void message.warning('不能移动到组织根目录');
      return;
    }

    try {
      const pass = await permissionCheck.check(
        items.map(item => ({
          id: item.id,
          type: item.type
        })),
      );

      if (!pass) {
        clear();
        clearSingleItem();
        setMoveOpen(false);
        return;
      }

      await moveMutation.mutateAsync({
        item: items,
        target_directory_id: targetId
      });

      clear();
      clearSingleItem();
      setMoveOpen(false);
    } catch (e) {}
  }

  async function handleRename(name: string) {
    if (!currentId) return;
    if (!singleItem) return;

    const pass = await permissionCheck.check([
      {
        id: singleItem.id,
        type: singleItem.type
      }
    ]);

    if (!pass) {
      setRenameOpen(false);
      clearSingleItem();
      return;
    }

    await directoryService.rename({
      item_id: singleItem.id,
      item_type: singleItem.type,
      new_name: name
    });

    await queryClient.invalidateQueries({
      queryKey: ['file-list', currentId]
    });

    setRenameOpen(false);
    clearSingleItem();
  }

  function clearSingleItem() {
    setSingleItem(null);
  }

  function openFileMeta(item: FileItem, readonly: boolean) {
    // if (item?.status === 2) {
    //   void message.warning('请等待文件解析完成');
    //   return;
    // }

    setMetaReadonly(readonly);
    setMetaFileInfo(item);
    setMetaModalTitle(readonly ? '文件详情' : '编辑文件信息');
    setMetaOpen(true);
  }

  const handleDirectoryLoaded = useCallback((directory: any) => {
    if (restoredRef.current) return;
    if (!initialPathIds.length) return;
    if (!directory?.full_path) return;

    const lastId = initialPathIds[initialPathIds.length - 1];

    if (String(directory.id) !== String(lastId)) return;

    const nextSegments = buildSegmentsFromPathIds({
      ids: initialPathIds,
      fullPath: directory.full_path,
      myFileRootId
    });

    restoredRef.current = true;
    restorePath(nextSegments);
  }, [initialPathIds, myFileRootId, restorePath]);

  function handleCloseSearch() {
    setSearchOpen(false);
    setSearchHasResult(false);
  }

  useEffect(() => {
    clear();
  }, [currentId, clear]);

  useEffect(() => {
    if (initialRestoreRef.current) return;
    if (!initialCurrentId) return;
    if (currentId) return;

    initialRestoreRef.current = true;

    setCurrentOnly({
      id: initialCurrentId,
      name: '',
      path: ''
    });
  }, [initialCurrentId, currentId, changePath, setCurrentOnly]);

  useEffect(() => {
    const userId = currentUser?.user_id;

    if (!userId) return;

    if (lastUserIdRef.current === null) {
      lastUserIdRef.current = userId;
      return;
    }

    if (String(lastUserIdRef.current) === String(userId)) return;

    lastUserIdRef.current = userId;

    resetPath();

    initialRestoreRef.current = false;
    restoredRef.current = false;

    setMyFileRootId(null);
    setAutoEntered(false);
  }, [currentUser, resetPath]);

  return (
    <>
      <div className="drive-app">
        {/*搜索弹窗*/}
        <SearchOverlay
          open={searchOpen}
          onClose={handleCloseSearch}
          onResultChange={setSearchHasResult}
          onJumpPath={(item: any) => {
            const ids = String(item.full_id_path || '')
              .split('/')
              .map((id: string) => id.trim())
              .filter(Boolean);

            if (ids.length && item.full_path) {
              const nextSegments = buildSegmentsFromPathIds({
                ids,
                fullPath: item.full_path,
                myFileRootId
              });

              restorePath(nextSegments);
            } else {
              changePath({
                id: item.directory_id || rootId,
                name: getPathName(item.full_path),
                path: item.full_path
              });
            }
          }}
        />

        {!searchHasResult && (
          <div className="drive-app__body">
            {/*左侧*/}
            <div className="drive-app__sidebar">
              <DirectoryTree
                mode="navigation"
                source="navigation"
                rootId={rootId}
                currentId={currentId}
                expandedIds={segments.map(item => item.id)}
                onRootLoaded={initRoot}
                onDefaultNodeLoaded={node => {
                  if (node.is_private) {
                    setMyFileRootId(node.id);
                  }

                  if (autoEntered) return;
                  if (currentId) return;

                  setAutoEntered(true);
                  changePath(node, 'tree');
                }}
                onSelect={(node: any) => {
                  if (node.segments?.length) {
                    restorePath(node.segments);
                    return;
                  }

                  changePath(node, 'tree');
                }}
              />
            </div>

            {/*右侧*/}
            <div className="drive-app__main">
              {/*顶部区域*/}
              {!searchOpen && (
                <div className="drive-app__header">
                  <div
                    className="drive-app__search-entry"
                    onClick={() => setSearchOpen(true)}
                  >
                    搜索文件、标签、描述...
                  </div>
                </div>
              )}

              {/*表格区域*/}
              <div className="drive-app__content">
                <div className="drive-app__header-path">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Breadcrumb
                      segments={segments}
                      onClick={node => changePath(node, 'breadcrumb')}
                    />

                    <Tooltip title="刷新">
                      <Button
                        type="text"
                        size="small"
                        icon={<ReloadOutlined/>}
                        className="drive-app__refresh-btn"
                        onClick={handleRefresh}
                      />
                    </Tooltip>
                  </div>

                  <Switch
                    checked={hideSharedContent}
                    loading={sharedStatusLoading}
                    checkedChildren="隐藏共享内容"
                    unCheckedChildren="显示共享内容"
                    onChange={handleSharedStatusChange}
                  />
                </div>

                {current && (
                  <FileList
                    parentId={currentId}
                    accessPath={accessPath}
                    isOrganizationLevel={isOrganizationLevel}
                    isMyFileLevel={isMyFileLevel}
                    currentUser={currentUser}
                    currentPath={current.path}
                    selection={selection}
                    scrollY={
                      searchOpen
                        ? 'calc(100vh - 650px)'
                        : 'calc(100vh - 285px)'
                    }
                    onDelete={handleDelete}
                    onMove={(item: FileItem) => {
                      setSingleItem(item);
                      setMoveOpen(true);
                    }}
                    onRename={(item: FileItem) => {
                      setSingleItem(item);
                      setRenameOpen(true);
                    }}
                    onEdit={(item: FileItem) => openFileMeta(item, false)}
                    onView={(item: FileItem) => openFileMeta(item, true)}
                    onEnterDirectory={changePath}
                    onUpdateFileMeta={(data: any) => updateFileMetaMutation.mutate(data)}
                    onDirectoryLoaded={(directory: any) => {
                      setCurrentDirectory(directory);
                      handleDirectoryLoaded(directory);
                    }}
                    onPermissionCheck={(items: PermissionCheckParams[]) =>
                      permissionCheck.check(items)
                    }
                    onShareScope={(record: any) => {
                      setShareRecord(record);
                      setShareModalOpen(true);
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/*浮动操作按钮*/}
      {!isOrganizationLevel && canWriteCurrentDirectory && (
        <FloatingAction
          onCreateFolder={() => setCreateOpen(true)}
          onUploadFile={() => {
            if (uploadOpen && uploadMinimized) {
              setUploadMinimized(false);
              void message.info('已恢复正在进行的上传任务');
              return;
            }

            setUploadMode('file');
            setUploadOpen(true);
          }}
          onUploadFolder={() => {
            if (uploadOpen && uploadMinimized) {
              setUploadMinimized(false);
              void message.info('已恢复正在进行的上传任务');
              return;
            }

            setUploadMode('folder');
            setUploadOpen(true);
          }}
        />
      )}

      <ActionBar
        count={selectedItems.length}
        onClear={clear}
        onDelete={() => handleDelete(selectedItems)}
        onMove={async () => {
          const pass = await permissionCheck.check(
            selectedItems.map(item => ({
              id: item.id,
              type: item.type
            }))
          );

          if (!pass) {
            clear();
            return;
          }

          clearSingleItem();
          setMoveOpen(true);
        }}
        loading={deleteMutation.isLoading}
      />

      {/*新建文件夹*/}
      <NameModal
        open={createOpen}
        title="新建文件夹"
        placeholder="请输入文件夹名称"
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
      />

      {current && (
        <UploadPanel
          mode={uploadMode}
          parentId={currentId}
          open={uploadOpen}
          minimized={uploadMinimized}
          onMinimize={() => setUploadMinimized(true)}
          onRestore={() => setUploadMinimized(false)}
          onCancel={closeUpload}
          onAIParseOpen={(fileInfo: any) => {
            setMetaReadonly(false);
            setMetaFileInfo(fileInfo);
            setMetaModalTitle('AI解析结果');
            setMetaOpen(true);
          }}
        />
      )}

      <MoveModal
        rootId={rootId}
        open={moveOpen}
        onCancel={() => setMoveOpen(false)}
        onOk={(target) => handleMove(target)}
      />

      {/*重命名*/}
      <NameModal
        open={renameOpen}
        title="重命名"
        placeholder="请输入新名称"
        initialValue={singleItem?.name}
        onCancel={() => {
          setRenameOpen(false);
          clearSingleItem();
        }}
        onOk={handleRename}
      />

      <FileMetaModal
        open={metaOpen}
        title={metaModalTitle}
        readonly={metaReadonly}
        fileInfo={metaFileInfo}
        onCancel={() => {
          setMetaOpen(false);
          setMetaFileInfo(null);
          setMetaReadonly(false);
        }}
        onSave={async (data: any) => {
          const pass = await permissionCheck.check([
            {
              id: data.id,
              type: data.type,
            }
          ]);

          if (!pass) {
            setMetaOpen(false);
            return;
          }

          return updateFileMetaMutation.mutateAsync(data);
        }}
      />

      <ShareScopeModal
        rootId={rootId}
        open={shareModalOpen}
        record={shareRecord}
        currentDirectoryId={currentId}
        onCancel={() => {
          setShareModalOpen(false);
          setShareRecord(null);
        }}
        onSuccess={() => {
          if (!currentId) return;

          void queryClient.invalidateQueries({
            queryKey: ['file-list', currentId]
          });
        }}
      />
    </>
  );
}